import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { environment } from '@env/environment';

/** ¿`url` va al mismo origen (esquema + host + puerto) y bajo la ruta de `base`? */
function sameBackend(url: string, base: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    const b = new URL(base);
    if (u.origin !== b.origin) return false;
    const prefix = b.pathname.replace(/\/+$/, '');
    return !prefix || u.pathname === prefix || u.pathname.startsWith(prefix + '/');
  } catch {
    return false;
  }
}

// Flag a nivel de módulo: si una respuesta 401 ya disparó logout, evitamos que
// peticiones posteriores en paralelo lo vuelvan a disparar y entremos en un
// loop interceptor → logout → navegación → guardia → etc.
let isLoggingOut = false;

/**
 * Interceptor único de la app: adjunta el Bearer a TODA petición dirigida a
 * nuestro backend. Es la contraparte de `ApiBaseService`: ningún servicio
 * vuelve a construir headers de auth a mano.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  // Allowlist de URLs base que pertenecen a NUESTRO backend.
  const backendBases: string[] = [
    (environment as { API_URL?: string }).API_URL,
    (environment as { CORE_API_URL?: string }).CORE_API_URL,
    (environment as { WS_TICKET_URL?: string }).WS_TICKET_URL,
  ].filter((u): u is string => typeof u === 'string' && u.trim().length > 0);

  // Comparación estricta por origen + ruta: `startsWith` de texto aceptaría
  // también "https://api.midominio.com.atacante.com/…" y le mandaría el token.
  const belongsToBackend = backendBases.some((base) => sameBackend(req.url, base));
  if (!belongsToBackend) {
    return next(req);
  }

  return from(authService.getIdToken()).pipe(
    switchMap((token) => {
      const authReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;
      return next(authReq);
    }),
    catchError((err: HttpErrorResponse) => {
      // Un 401 NO siempre significa sesión expirada: el backend también puede
      // responder 401 en errores de autorización/negocio con la sesión aún
      // vigente. Solo cerramos sesión si la sesión realmente ya no es válida.
      if (err.status === 401 && !isLoggingOut) {
        return from(authService.isLoggedIn()).pipe(
          switchMap((stillValid) => {
            if (!stillValid) {
              isLoggingOut = true;
              authService
                .logOut()
                .catch(() => undefined)
                .finally(() => {
                  if (router.url !== '/' && router.url !== '/login') {
                    router.navigateByUrl('/');
                  }
                  setTimeout(() => {
                    isLoggingOut = false;
                  }, 1500);
                });
            }
            return throwError(() => err);
          }),
          catchError(() => throwError(() => err))
        );
      }
      return throwError(() => err);
    })
  );
};
